import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {CardStoreService} from './card-store.service';
import {CardNote, CardNoteType, CustomObjects, NoteType, RelationNote} from './custom-objects';
import {ObjectExtractingServiceService} from './object-extracting-service.service';
import {MessageFactoryService} from './message-factory.service';
import {DeckType} from './card-deck';

@Injectable({
  providedIn: 'root'
})
export class CustomObjectsService {

  public customObjects: CustomObjects;
  public co$: BehaviorSubject<CustomObjects>;

  constructor(private cs: CardStoreService,
              private ccs: ObjectExtractingServiceService,
              private mfs: MessageFactoryService) {
    this.cs.getUserLoginListener().subscribe(b => {
      if (b === undefined) {
        this.customObjects = undefined;
      }
    });
    this.initCustomObjects();
  }

  public initCustomObjects() {
    this.customObjects = {
      id: 1,
      cardNotes: [],
      relationNotes: []
    };
    this.co$ = new BehaviorSubject<CustomObjects>(this.customObjects);
  }

  loadCustomObjectsByServer(customObjects: CustomObjects) {
    console.log('::LOAD CUSTOMOBJECTS');

    if (!customObjects) {
      console.warn('Received null or undefined customObjects');
      return;
    }

    this.customObjects = {
      id: customObjects.id ?? 1,  // Fallback to a default ID if undefined
      cardNotes: customObjects.cardNotes ?? [],  // Ensure it's always an array
      relationNotes: customObjects.relationNotes ?? []  // Ensure it's always an array
    };

    if (this.co$) {
      this.co$.next(this.customObjects);
    } else {
      this.co$ = new BehaviorSubject<CustomObjects>(this.customObjects);
    }
  }

  public getCustomObjects() {
    if (this.customObjects === undefined) {
      throw new Error('No CustomObjects initialized');
    }
    return this.customObjects;
  }

  public getCustomObjectsBehaviorSubject(): BehaviorSubject<CustomObjects> {
    if (this.co$ === undefined) {
      this.co$ = new BehaviorSubject<CustomObjects>(this.customObjects);
    }
    return this.co$;
  }


  // CARD NOTE FUNCTIONALITY
  public addOrUpdateCardNote(
    noteType: CardNoteType,
    text: string,
    sourceId: number,
    sourceName: string,
    sourceDeckType: DeckType,
    sourceCardDeckId: number,
    sendWebSocketMessage = true)
  {
    let addedNote: boolean = undefined;
    if (this.hasCardNote(sourceId)) {
      const existingNote: CardNote = this.getCardNote(sourceId);
      existingNote.type = noteType;
      existingNote.text = text;
      addedNote = false;
    } else {
      const note: CardNote = {
        type: noteType,
        text,
        sourceId
      };
      this.customObjects.cardNotes.push(note);
      addedNote = true;
    }
    this.co$.next(this.customObjects);

    if (sendWebSocketMessage) {
      // CALL WEBSOCKET CONNECTION
      const cardNoteObject = this.mfs.buildCardNoteObject(noteType, text);
      const cardObject = this.mfs.buildCardObject(sourceCardDeckId, sourceId, sourceDeckType, sourceName);
      let messageObject = undefined;
      if (addedNote) {
        messageObject = this.mfs.createMessageAddCardNote(cardObject, cardNoteObject);
      }else {
        messageObject = this.mfs.createMessageChangeCardNote(cardObject, cardNoteObject);
      }
      this.ccs.handleAction(messageObject);
    }
  }

  public removeCardNote(
    sourceId: number,
    sourceCardPatternName: string,
    sourceCardDeckId: number,
    sourceCardDeckType: DeckType,
    sendWebSocketMessage = true) {
    const noteToDelete = this.getCardNote(sourceId);
    this.customObjects.cardNotes = this.customObjects.cardNotes.filter(note => note.sourceId !== sourceId);
    this.co$.next(this.customObjects);

    if (sendWebSocketMessage) {
      // CALL WEBSOCKET CONNECTION
      const cardNoteObject = this.mfs.buildCardNoteObject(noteToDelete.type, noteToDelete.text);
      const cardObject = this.mfs.buildCardObject(sourceCardDeckId, sourceId, sourceCardDeckType, sourceCardPatternName);
      const messageObject = this.mfs.createMessageDeleteCardNote(cardObject, cardNoteObject);
      this.ccs.handleAction(messageObject);
    }
  }

  public getCardNote(sourceId: number): CardNote {
    return this.customObjects.cardNotes.find((note) => note.sourceId === sourceId);
  }


  // RELATION NOTE FUNCTIONALITY
  public addOrUpdateRelationNote(
    text: string,
    sourceId: number,
    targetId: number,
    sourceName: string,
    targetName: string,
    sourceDeckType: DeckType,
    targetDeckType: DeckType,
    sendWebSocketMessage = true) {
    let relationNoteAdded: boolean = undefined;
    let noteType = undefined;
    if (this.hasRelationNote(sourceId, targetId)) {
      const existingNote: RelationNote = this.customObjects.relationNotes.find(
        (rn) => rn.sourceId === sourceId && rn.targetId === targetId);
      existingNote.text = text;
      relationNoteAdded = false;
      noteType = existingNote.type;
    } else {
      const note: RelationNote = {
        type: NoteType.RELATION_ELEMENT,
        text,
        sourceId,
        targetId
      };
      this.customObjects.relationNotes.push(note);
      relationNoteAdded = true;
      noteType = note.type;
    }
    this.co$.next(this.customObjects);

    if (sendWebSocketMessage) {
      // CALL WEBSOCKET CONNECTION
      const linkNoteObject = this.mfs.buildLinkNoteObject(noteType, text);
      const linkObject = this.mfs.buildLinkObject(sourceId, targetId, sourceDeckType, targetDeckType, sourceName, targetName);
      let messageObject = undefined;
      if (relationNoteAdded) {
        messageObject = this.mfs.createMessageAddLinkNote(linkObject, linkNoteObject);
      }else {
        messageObject = this.mfs.createMessageUpdateLinkNote(linkObject, linkNoteObject);
      }
      this.ccs.handleAction(messageObject);
    }
  }

  public removeRelationNote(
    sourceId: number,
    targetId: number,
    sourceDeckType: DeckType,
    targetDeckType: DeckType,
    sourceName: string,
    targetName: string,
    sendWebSocketMessage = true) {
    const index = this.customObjects.relationNotes.findIndex(note => (note.sourceId === sourceId && note.targetId === targetId));
    if (index > -1) {
      const noteToDelete = this.customObjects.relationNotes[index];
      const noteType = noteToDelete.type;
      const noteText = noteToDelete.text;
      this.customObjects.relationNotes.splice(index, 1);

      if (sendWebSocketMessage) {
        // CALL WEBSOCKET CONNECTION
        const linkNoteObject = this.mfs.buildLinkNoteObject(noteType, noteText);
        const linkObject = this.mfs.buildLinkObject(sourceId, targetId, sourceDeckType, targetDeckType, sourceName, targetName);
        const messageObject = this.mfs.createMessageDeleteLinkNote(linkObject, linkNoteObject);
        this.ccs.handleAction(messageObject);
      }
    }
    this.co$.next(this.customObjects);
  }

  public getRelationNote(sourceId: number, targetId: number): RelationNote {
    return this.customObjects.relationNotes.find(rn => rn.sourceId === sourceId && rn.targetId === targetId);
  }

  hasCardNote(selectedCardId: number) {
    if (this.customObjects) {
      return this.customObjects.cardNotes.some(rn => rn.sourceId === selectedCardId);
    } else {
      return false;
    }
  }

  hasRelationNote(sourceId: number, targetId: number) {
    if (this.customObjects){
    return this.customObjects.relationNotes
      .some(rn => rn.sourceId === sourceId && rn.targetId === targetId);
  }else {
      return false;
    }
  }

  clearCustomObjects() {
    this.customObjects.cardNotes.splice(0, this.customObjects.cardNotes.length);
    this.customObjects.relationNotes.splice(0, this.customObjects.relationNotes.length);
  }
}
